
import { handleAxiosException } from '@/api'
import { FindByIdMyArqanaHorseRegistrationDto, myRegistrationApi, RegistrationDocumentDto } from '@/api/auth/myHorseRegistration'
import snackbarStore from '@/store/snackbarStore'
import { Wrapper } from '@/types'
import { DocumentType } from '@/utils/documentTypes'
import { getDocumentRejectReasonLabel } from '@/utils/documentRejectReasons'
import Vue from 'vue'
import TitlePage from '@/components/TitlePage.vue'
import downloadFile from '@/utils/downloadFile'
import RegistrationStepper from '../RegistrationStepper.vue'
import { DocumentRuleType, documentsRule } from './rules'

export default Vue.extend({
  components: { RegistrationStepper, TitlePage },
  props: {
    registrationId: {
      required: true,
      type: Number,
    },
  },
  data() {
    return {
      loading: false,
      sendingDocument: undefined as DocumentType | undefined,
      error: '',
      registration: {} as FindByIdMyArqanaHorseRegistrationDto,
      compulsoryDocuments: [] as DocumentType[],
      optionalDocuments: [] as DocumentType[],
      mappedDocuments: {} as Record<string, RegistrationDocumentDto>,
      files: {} as Partial<Record<DocumentType, Wrapper<File>>>,
      errorFiles: {} as Partial<Record<DocumentType, Wrapper<File>>>,
      documentsToHide: ['EXPORT_SANITARY_CERTIFICATE_JPN_SELLER', 'EXPORT_SANITARY_CERTIFICATE_USA_SELLER', 'CERTIFICAT_SANITAIRE_ARQANA'] as DocumentType[],
    }
  },
  methods: {
    getDocumentRejectReasonLabel,
    async load() {
      this.loading = true
      try {
        this.registration = await myRegistrationApi.findById(this.registrationId)
        const rules = documentsRule[this.registration.type]
        if (!rules) {
          this.error = 'Une erreur inconnue est survenu. Veuillez contacter l\'administrateur du site'
        } else {
          const documents = (Object.keys(rules) as DocumentType[]).filter((d: DocumentType) => {
            if (d === 'DOSSIER_VETERINAIRE' || d === 'CHEVAL_ETRANGER') {
              return false
            }
            switch (rules[d as DocumentType] as DocumentRuleType) {
              case 'OPTIONAL_IF_FEMALE':
                return this.registration.gender === 'FEMELLE'
              case 'OPTIONAL_IF_MALE':
                return this.registration.gender === 'MALE'
              case 'OPTIONAL_IF_TROT':
                return this.isTrot()
              default:
                return true
            }
          })
          this.compulsoryDocuments = documents.filter((d: DocumentType) => {
            switch (rules[d as DocumentType] as DocumentRuleType) {
              case 'COMPULSORY':
                return true
              case 'COMPULSORY_IF_COVERED':
                return this.registration.protrusionFormula === 'DEFAULT' || this.registration.protrusionFormula === 'FIRST'
              default:
                return (d === 'CONTRAT_FG_BUYER' || d === 'CONTRAT_FG_SELLER') && (this.registration.associationContract || this.registration.rentalContract)
            }
          })
          this.optionalDocuments = documents.filter((i) => this.compulsoryDocuments.indexOf(i) < 0)
          this.mappedDocuments = this.registration.documents.reduce((p, v) => {
            // eslint-disable-next-line no-param-reassign
            p[v.type] = v
            return p
          }, {} as Record<string, RegistrationDocumentDto>)
          this.files = (Object.keys(rules) as DocumentType[]).reduce((m, v) => {
            // eslint-disable-next-line no-param-reassign
            m[v] = { value: undefined }
            return m
          }, {} as Partial<Record<DocumentType, Wrapper<File>>>)
        }
      } catch (e) {
        this.error = handleAxiosException(e)
      }
      this.loading = false
    },
    async sendDocument(d: DocumentType) {
      this.sendingDocument = d
      try {
        const file = this.files[d]?.value
        if (file) {
          const formData = new FormData()

          if (Array.isArray(file)) {
            file.forEach((f) => {
              formData.append('files', f)
            })
          } else {
            formData.append('files', file)
          }

          await myRegistrationApi.sendDocument(this.registrationId, d, formData).then(() => {
            Vue.set(this.errorFiles, d, undefined)
          }).catch((e) => {
            Vue.set(this.errorFiles, d, this.$t(handleAxiosException(e)))
            throw e
          })
          snackbarStore.setSnackbar({
            text: this.$t('success.send_files') as string,
            color: 'success',
          })
        } else {
          Vue.set(this.errorFiles, d, undefined)
        }
      } catch (ex) {
        snackbarStore.setSnackbar({
          text: this.$t('error.failed_files') as string,
          color: 'error',
        })
      }
      this.sendingDocument = undefined
      this.load()
    },
    async downloadDocument(type: DocumentType) {
      myRegistrationApi.getDocument(this.registrationId, type).then((v) => {
        downloadFile(v.downloadUrl, v.name)
      }).catch((e) => {
        snackbarStore.setSnackbar({ text: this.$t(handleAxiosException(e)) as string, color: 'error' })
      })
    },
    getTooltips(type: DocumentType) {
      const res = []
      switch (type) {
        case 'COPIE_DOCUMENT_ACCOMPAGNEMENT':
          res.push('livret')
          break
        case 'PIROPLASMOSE_BUYER':
        case 'PIROPLASMOSE_SELLER':
          res.push('piroplasmose')
          res.push('test-sanitaire')
          break
        case 'ARTERITE':
          res.push('arterite')
          res.push('test-sanitaire')
          break
        case 'METRITE':
          res.push('metrite')
          res.push('test-sanitaire')
          break
        case 'COGGINS':
          res.push('coggins')
          res.push('test-sanitaire')
          break
        case 'TITRE_PROPRIETE_BUYER':
        case 'TITRE_PROPRIETE_SELLER':
          res.push('immatriculation')
          break
        case 'REPRODUCTION':
          res.push(this.registration.mareFull ? 'reproduction-pleine' : 'reproduction-vide')
          break
        case 'CONTRAT_FG_SELLER':
          res.push('contrat-fg')
          break
        default:
      }
      return res
    },
    disableSendDocument(d: DocumentType) {
      return !this.files[d]?.value
          || !!this.sendingDocument && this.sendingDocument !== d
          || d === 'REPRODUCTION' && this.reproductionClosed
          || d === 'COPIE_DOCUMENT_ACCOMPAGNEMENT' && (new Date(this.registration.technicalSale.startDate).getTime() - new Date().getTime()) < 6 * 24 * 60 * 60 * 1000
    },
    disableFileInput(d: DocumentType) {
      return !!this.sendingDocument
          || (this.mappedDocuments[d] && this.mappedDocuments[d].status === 'ACCEPTED')
          || d === 'REPRODUCTION' && this.reproductionClosed
          || d === 'COPIE_DOCUMENT_ACCOMPAGNEMENT' && (new Date(this.registration.technicalSale.startDate).getTime() - new Date().getTime()) < 6 * 24 * 60 * 60 * 1000
          || Object.keys(this.files).filter(e => this.files[e as DocumentType]?.value && e !== d).length > 0
    },
    isTrot(): boolean {
      return !!process.env.VUE_APP_IS_TROT
    },
  },
  computed: {
    reproductionClosed(): boolean {
      return this.registration.mareFull && (new Date(this.registration.exposingDay).getTime() - new Date().getTime()) >= 30 * 24 * 60 * 60 * 1000
    },
  },
  mounted() {
    this.load()
  },
})
